import { useEffect, useState } from "react"
import { useNetwork } from "@daily-co/daily-react"
import type { DailyNetworkStatsData } from "@daily-co/daily-js"
import { Wifi, WifiHigh, WifiLow, X } from "lucide-react"

export type NetworkThreshold = "good" | "low" | "very-low"

/** Refresh interval for Daily's network statistics while the panel is open. */
export const NETWORK_STATS_POLL_MS = 2000

export const describeNetworkThreshold = (threshold: NetworkThreshold) =>
  threshold === "good" ? "Good" : threshold === "low" ? "Low" : "Very low"

export const formatBitrate = (bitsPerSecond: number | null | undefined) => {
  if (bitsPerSecond === null || bitsPerSecond === undefined || !Number.isFinite(bitsPerSecond)) return "–"
  if (bitsPerSecond >= 1_000_000) return `${(bitsPerSecond / 1_000_000).toFixed(1)} Mbps`
  return `${Math.round(bitsPerSecond / 1000)} kbps`
}

export const formatPercent = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined || !Number.isFinite(fraction) ? "–" : `${(fraction * 100).toFixed(1)}%`

export const formatMilliseconds = (seconds: number | null | undefined) =>
  seconds === null || seconds === undefined || !Number.isFinite(seconds) ? "–" : `${Math.round(seconds * 1000)} ms`

export function NetworkQualityIcon({ threshold, className }: { threshold: NetworkThreshold; className?: string }) {
  if (threshold === "good") return <Wifi className={className} />
  if (threshold === "low") return <WifiHigh className={className} />
  return <WifiLow className={className} />
}

export const networkThresholdTone = (threshold: NetworkThreshold) =>
  threshold === "good" ? "text-emerald-300" : threshold === "low" ? "text-amber-300" : "text-red-300"

type NetworkPanelProps = {
  onClose: () => void
}

/**
 * Network quality and live statistics from Daily's `getNetworkStats()`, the information
 * Prebuilt shows in its network sidebar. Polls while open.
 */
export function NetworkPanel({ onClose }: NetworkPanelProps) {
  const { threshold, quality, getStats, networkState, networkStateReasons } = useNetwork()
  const [stats, setStats] = useState<DailyNetworkStatsData["latest"] | null>(null)

  useEffect(() => {
    let active = true
    const refresh = async () => {
      try {
        const data = await getStats()
        if (active && data && "latest" in data) setStats(data.latest)
      } catch {
        // Stats are best effort; the quality label still renders.
      }
    }
    void refresh()
    const interval = window.setInterval(() => void refresh(), NETWORK_STATS_POLL_MS)
    return () => {
      active = false
      window.clearInterval(interval)
    }
  }, [getStats])

  const rows: Array<[string, string]> = [
    ["Round trip time", formatMilliseconds(stats?.networkRoundTripTime)],
    ["Sending", formatBitrate(stats?.sendBitsPerSecond)],
    ["Receiving", formatBitrate(stats?.recvBitsPerSecond)],
    ["Available upload", formatBitrate(stats?.availableOutgoingBitrate)],
    ["Packet loss (send)", formatPercent(stats?.totalSendPacketLoss)],
    ["Packet loss (receive)", formatPercent(stats?.totalRecvPacketLoss)],
  ]
  const reasons = (networkStateReasons || []).filter(Boolean)

  return (
    <div
      role="dialog"
      aria-label="Network quality"
      data-testid="call-network-panel"
      className="absolute right-3 top-3 z-[45] w-[min(20rem,calc(100vw-1.5rem))] space-y-3 rounded-2xl bg-[#0f1419] p-4 text-white shadow-2xl ring-1 ring-white/10"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Network</p>
        <button type="button" aria-label="Close network panel" onClick={onClose} className="rounded-md p-1 text-white/70 hover:bg-white/10 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
      <p data-testid="call-network-quality" className={`flex items-center gap-2 text-sm font-medium ${networkThresholdTone(threshold)}`}>
        <NetworkQualityIcon threshold={threshold} className="h-4 w-4" />
        {describeNetworkThreshold(threshold)} connection
        <span className="text-xs font-normal text-white/50">({Math.round(quality)}/100{networkState && networkState !== "unknown" ? `, ${networkState}` : ""})</span>
      </p>
      {reasons.length > 0 && (
        <p className="text-xs text-amber-200">Affected by: {reasons.join(", ")}</p>
      )}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-white/60">{label}</dt>
            <dd className="text-right font-medium tabular-nums text-white">{value}</dd>
          </div>
        ))}
      </dl>
      {!stats && <p className="text-[11px] text-white/40">Statistics arrive a few seconds after joining.</p>}
    </div>
  )
}
